<template>
  <v-dialog v-model="visible" max-width="680" data-cy="approve-agent-dialog">
    <v-card v-if="agent">
      <v-card-title data-cy="approve-dialog-title">{{ title }}</v-card-title>

      <v-card-text>
        <AgentIdentity :agent="agent" />

        <template v-if="step === 'permissions'">
          <div class="font-weight-medium mt-4">What people at your firm may grant it</div>
          <p class="text-caption text-grey-darken-1 mb-2">
            A ceiling, not a grant. Nothing connects by approving, and each person still approves their
            own permissions.
          </p>

          <LlRadioGroup v-model="formState.cap" :options="capOptions" data-cy="cap-choice" />

          <div
            class="ml-6 pl-3 border-l grid grid-cols-1 sm:grid-cols-2 gap-x-6"
            :class="{ 'opacity-50 pointer-events-none': formState.cap !== 'some' }"
            data-cy="scope-picker"
          >
            <div v-for="column in columns" :key="column.key" :data-cy="`scope-column-${column.key}`">
              <template v-for="group in column.groups" :key="group.heading">
                <div v-if="group.items.length" class="text-caption text-grey-darken-1 mt-3">
                  {{ group.heading }}
                </div>
                <LlCheckbox
                  v-for="permission in group.items"
                  :key="permission.scope"
                  :model-value="isTicked(permission.scope)"
                  :disabled="isLocked(permission.scope)"
                  :label="permission.label"
                  :data-cy="`scope-${permission.scope}`"
                  @update:model-value="toggleScope(permission.scope, $event)"
                />
              </template>
            </div>
          </div>

        </template>

        <template v-else>
          <div class="text-caption text-grey-darken-1 mt-4">
            People at your firm will be able to grant it
          </div>
          <p v-if="!chosenScopes.length || formState.cap === 'all'" class="mb-0" data-cy="confirm-summary">
            Anything this agent asks for, up to what LeanLaw allows it{{
              explicitPermissions.length ? ', including sending email' : '' }}.
          </p>
          <ul v-else class="ml-4 mt-1" data-cy="confirm-summary">
            <li v-for="label in chosenLabels" :key="label">{{ label }}</li>
          </ul>

          <v-alert type="success" variant="tonal" density="compact" class="mt-4 text-body-2">
            Nothing is connected by approving and no data moves. Each person still approves their own
            permissions from inside the agent, and cannot exceed what you set here.
          </v-alert>

          <p class="text-caption text-grey-darken-1 mt-3 mb-0">
            Recorded against your name on the firm's audit trail.
          </p>
        </template>

        <p v-if="uiState.error" class="text-error text-body-2 mt-3 mb-0" data-cy="approve-error">
          {{ uiState.error }}
        </p>
      </v-card-text>

      <v-card-actions class="pa-6">
        <LlSecondaryButton v-if="step === 'confirm'" @click="step = 'permissions'">Back</LlSecondaryButton>
        <v-spacer />
        <LlSecondaryButton @click="close">Cancel</LlSecondaryButton>
        <LlSecondaryButton
          v-if="step === 'permissions'"
          color="primary"
          data-cy="approve-continue"
          @click="toConfirm"
        >
          {{ agent.status === 'approved' ? 'Review changes' : 'Continue' }}
        </LlSecondaryButton>
        <LlSecondaryButton
          v-else
          color="primary"
          :loading="saving"
          data-cy="approve-confirm"
          @click="submit"
        >
          Approve
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlRadioGroup from '@/components/LlRadioGroup.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import AgentIdentity from './AgentIdentity.vue';
import type { AgentAccessAgent, AgentAccessPermission } from '../models/agentAccessTypes';

const props = defineProps<{
  modelValue: boolean;
  agent: AgentAccessAgent | null;
  permissions: AgentAccessPermission[];
  saving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'approve', payload: { clientId: string; allowedScopes: string[] | null }): void;
}>();

const capOptions = [
  { label: 'Anything the agent asks for', value: 'all' },
  { label: 'Only these permissions', value: 'some' },
];

const step = ref<'permissions' | 'confirm'>('permissions');
const formState = reactive({ cap: 'all', scopes: [] as string[] });
const uiState = reactive({ error: '' });

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const title = computed(() => {
  if (!props.agent) return '';
  if (step.value === 'confirm') return `Approve ${props.agent.name} for your firm?`;
  return props.agent.status === 'approved' ? `Permissions for ${props.agent.name}` : `Approve ${props.agent.name}`;
});

// The everything-permissions. `write:*` covers the reads too, so it subsumes `read:*` as well.
const READ_ALL = 'read:*';
const WRITE_ALL = 'write:*';
const OFFLINE_ACCESS = 'offline_access';

const isEverything = (scope: string) => scope === READ_ALL || scope === WRITE_ALL;

// A scope with no catalogue entry is still listed, by its raw name — the same rule
// ConnectionPermissionsDialog follows. Dropping it would leave a saved cap the dialog re-submits
// without ever having shown it, which is the one direction this screen must never err in.
const describe = (scope: string): AgentAccessPermission =>
  props.permissions.find((p) => p.scope === scope)
  ?? ({ id: '', scope, label: scope, description: '', isWrite: false, requiresExplicitGrant: false } as AgentAccessPermission);

// A wildcard is the group's headline choice, so it leads rather than trailing the list it covers.
const wildcardFirst = (items: AgentAccessPermission[]) =>
  [...items].sort((a, b) => Number(isEverything(b.scope)) - Number(isEverything(a.scope)));

// A retired scope, or a cap saved against a newer catalogue than the one cached here.
const uncatalogued = computed(() =>
  formState.scopes.filter((s) => !props.permissions.some((p) => p.scope === s)).map(describe));

const groups = computed(() => {
  const reads = props.permissions.filter((p) => !p.isWrite && p.scope.startsWith('read:'));
  const session = props.permissions.filter((p) => !p.isWrite && !p.scope.startsWith('read:'));
  return [
    { heading: 'See', items: wildcardFirst(reads) },
    {
      heading: 'Add and change',
      items: wildcardFirst(props.permissions.filter((p) => p.isWrite && !p.requiresExplicitGrant)),
    },
    { heading: 'Send email', items: explicitPermissions.value },
    { heading: 'Session', items: session },
    { heading: 'No longer offered', items: uncatalogued.value },
  ];
});

// Seeing on the left; everything that acts, plus the session, on the right.
const columns = computed(() => [
  { key: 'see', groups: groups.value.slice(0, 1) },
  { key: 'act', groups: groups.value.slice(1) },
]);

// Mirrors AgentPermissionCatalog.Expand on the server, which excludes rather than includes: everything
// in the catalogue except offline_access and the permissions that have to be named, which a wildcard
// never implies. Stated the same way round, so a catalogue entry added later defaults to covered rather
// than to overlooked.
const coversResource = (p: AgentAccessPermission) =>
  !isEverything(p.scope) && p.scope !== OFFLINE_ACCESS && !p.requiresExplicitGrant;

// Permissions no wildcard reaches, and that a firm approval with no cap (null) withholds on the server.
// "Anything the agent asks for" still means them too, so it names them rather than sending null.
const explicitPermissions = computed(() => props.permissions.filter((p) => p.requiresExplicitGrant));

const isExplicit = (scope: string) => explicitPermissions.value.some((p) => p.scope === scope);

// Staying connected is always inside the firm's ceiling and cannot be excluded from it. It grants no
// data of its own, and a ceiling without it would make every connection at the firm expire into a
// re-authentication loop. This is a cap, not a grant: each person still declines it for themselves.
const isAlwaysCapped = (scope: string) => scope === OFFLINE_ACCESS;

const alwaysCapped = computed(() =>
  props.permissions.filter((p) => isAlwaysCapped(p.scope)).map((p) => p.scope));

const impliedScopes = computed(() => {
  if (formState.scopes.includes(WRITE_ALL)) {
    return new Set([READ_ALL, ...props.permissions.filter(coversResource).map((p) => p.scope)]);
  }
  if (formState.scopes.includes(READ_ALL)) {
    return new Set(props.permissions.filter((p) => coversResource(p) && !p.isWrite).map((p) => p.scope));
  }
  return new Set<string>();
});

// An agent can only address a report by resolving the firm's people to ids, so allowing email without
// allowing that would hand out a permission nobody can use.
const REQUIRED_WITH: Record<string, string[]> = { 'send:emails': ['read:users'] };

const requiredScopes = computed(() => new Set(
  formState.scopes
    .flatMap((s) => REQUIRED_WITH[s] ?? [])
    .filter((s) => props.permissions.some((p) => p.scope === s) && !impliedScopes.value.has(s))));

const isLocked = (scope: string) =>
  formState.cap !== 'some' || isAlwaysCapped(scope) || impliedScopes.value.has(scope)
  || requiredScopes.value.has(scope);

const isTicked = (scope: string) =>
  formState.cap === 'all' || isAlwaysCapped(scope) || formState.scopes.includes(scope)
  || impliedScopes.value.has(scope) || requiredScopes.value.has(scope);

// The permissions the administrator actually decided on. A wildcard already carries what it implies,
// and sending both would record a ceiling that reads as narrower than it is the moment the catalogue
// grows a resource. Staying connected is not a decision, so it is not counted as one here — otherwise
// a cap of nothing but offline_access would satisfy the "pick at least one" guard below.
const chosenScopes = computed(() =>
  formState.scopes.filter((s) => !impliedScopes.value.has(s) && !isAlwaysCapped(s)));

const cappedScopes = computed(() => [
  ...chosenScopes.value,
  ...Array.from(requiredScopes.value).filter((s) => !chosenScopes.value.includes(s)),
  ...alwaysCapped.value,
]);

const chosenLabels = computed(() => cappedScopes.value.map((s) => describe(s).label));

// Seeds the cap from the agent, and reseeds on every open: reopening on a different agent must not
// carry the previous one's cap in with it. `immediate` covers a dialog mounted already open.
watch(
  () => [props.modelValue, props.agent?.clientId],
  ([open]) => {
    if (!open) return;
    step.value = 'permissions';
    uiState.error = '';
    const saved = props.agent?.allowedScopes ?? [];
    // An approval saved with no cap reaches everything except the named permissions, which "anything"
    // now includes, so it reopens as what it really holds: everything, with those left to tick.
    if (props.agent?.status === 'approved' && !saved.length && explicitPermissions.value.length) {
      formState.cap = 'some';
      formState.scopes = [WRITE_ALL];
      return;
    }
    // write:* plus the named permissions is how "anything" is saved, so it reopens as that.
    const rest = saved.filter((s) => !isExplicit(s) && !isAlwaysCapped(s));
    const everythingPlusExplicit = rest.length === 1 && rest[0] === WRITE_ALL && saved.some(isExplicit);
    formState.cap = !saved.length || everythingPlusExplicit ? 'all' : 'some';
    formState.scopes = [...saved];
  },
  { immediate: true },
);

// LlCheckbox is a boolean control, so the set is kept here rather than bound per box.
const toggleScope = (scope: string, checked: boolean) => {
  formState.scopes = checked
    ? [...formState.scopes, scope]
    : formState.scopes.filter((s) => s !== scope);
  uiState.error = '';
};

const close = () => {
  visible.value = false;
};

const toConfirm = () => {
  if (formState.cap === 'some' && !chosenScopes.value.length) {
    uiState.error = 'Pick at least one permission, or choose "Anything the agent asks for".';
    return;
  }
  uiState.error = '';
  step.value = 'confirm';
};

const submit = () => {
  if (!props.agent) return;
  // null, never []: the API reads an empty list as "no ceiling", so an empty array would widen the
  // cap to everything rather than narrowing it to nothing. No ceiling withholds the named permissions,
  // so where any exist "anything" is sent as write:* plus those — everything, with them added.
  const everything = explicitPermissions.value.length
    ? [WRITE_ALL, ...explicitPermissions.value.map((p) => p.scope), ...alwaysCapped.value]
    : null;
  emit('approve', {
    clientId: props.agent.clientId,
    allowedScopes: formState.cap === 'all' ? everything : [...cappedScopes.value],
  });
};
</script>
